import "@hotwired/turbo-rails"
Turbo.config.forms.mode = "optin"

import { guardTurboViewRender, isStaleTurboRendererError } from "../turboViewRenderGuard"
guardTurboViewRender()
if (typeof Honeybadger !== "undefined") {
  Honeybadger.beforeNotify((notice) => {
    if (notice && isStaleTurboRendererError(notice.message)) return false
  })
}

import Median from "median-js-bridge"
window.Median = Median

import { initClarity, identifyClarityUser } from "../clarity"
initClarity()
identifyClarityUser()
document.addEventListener("turbo:load", identifyClarityUser)

import { Application } from "@hotwired/stimulus"

import ChatController from "../controllers/front_end/chat_controller"
import ChooseRoundsController from "../controllers/front_end/choose_rounds_controller"
import CommunityFinderController from "../controllers/front_end/community_finder_controller"
import GolfLifeVideoOverlayController from "../controllers/front_end/golf_life_video_overlay_controller"
import FeedbackController from "../controllers/front_end/feedback_controller"
import LeaguesController from "../controllers/front_end/leagues_controller"
import LeagueShareController from "../controllers/front_end/league_share_controller"
import LocationController from "../controllers/front_end/location_controller"
import MemberSearchController from "../controllers/front_end/member_search_controller"
import MobileNavigationController from "../controllers/front_end/mobile_navigation_controller"
import PlayersController from "../controllers/front_end/players_controller"
import S3VideoOverlayController from "../controllers/front_end/s3_video_overlay_controller"
import StandingsController from "../controllers/front_end/standings_controller"
import TabsController from "../controllers/front_end/tabs_controller"
import VideoOverlayController from "../controllers/front_end/video_overlay_controller"
import CourseAutocompleteController from "../controllers/course_autocomplete_controller"
import EventsPortalActivityScheduleController from "../controllers/front_end/events_portal_activity_schedule_controller"
import EventsPortalSidebarController from "../controllers/front_end/events_portal_sidebar_controller"

window.Stimulus = Application.start()
window.Stimulus.handleError = (error, message, detail) => {
  console.warn(message, detail)
  if (typeof Honeybadger !== "undefined") {
    Honeybadger.notify(error)
  }
}
Stimulus.register("chat", ChatController)
Stimulus.register("choose-rounds", ChooseRoundsController)
Stimulus.register("community-finder", CommunityFinderController)
Stimulus.register("feedback", FeedbackController)
Stimulus.register("golf-life-video-overlay", GolfLifeVideoOverlayController)
Stimulus.register("leagues", LeaguesController)
Stimulus.register("league-share", LeagueShareController)
Stimulus.register("location", LocationController)
Stimulus.register("member-search", MemberSearchController)
Stimulus.register("mobile-navigation", MobileNavigationController)
Stimulus.register("players", PlayersController)
Stimulus.register("s3-video-overlay", S3VideoOverlayController)
Stimulus.register("standings", StandingsController)
Stimulus.register("tabs", TabsController)
Stimulus.register("video-overlay", VideoOverlayController)
Stimulus.register("course-autocomplete", CourseAutocompleteController)
Stimulus.register("events-portal-activity-schedule", EventsPortalActivityScheduleController)
Stimulus.register("events-portal-sidebar", EventsPortalSidebarController)

import React from "react"
import { createRoot } from "react-dom/client"

import ChangeLocation from "../components/FrontEnd/ChangeLocation"
import GiftCard from "../components/GiftCard"
import HoleSponsorForm from "../components/HoleSponsorForm"
import LeaderboardScreen from "../components/LeaderboardScreen"
import NewFeedPostComment from "../components/NewFeedPostComment"
import NewFeedPostScreen from "../components/NewFeedPostScreen"
import PastLeagues from "../components/FrontEnd/PastLeagues"
import RoundScreen from "../components/RoundScreen"
import UploadPhoto from "../components/UploadPhoto"

const components = {
  ChangeLocation,
  GiftCard,
  HoleSponsorForm,
  LeaderboardScreen,
  NewFeedPostComment,
  NewFeedPostScreen,
  PastLeagues,
  RoundScreen,
  UploadPhoto
}

import { QueryClient } from "@tanstack/react-query"

window.SparkGolfQueryClient = new QueryClient()

import Swiper from "swiper/bundle"
window.Swiper = Swiper

import "core-js"
import "whatwg-fetch"
import Cookies from "js-cookie"
import { destroyPullToRefresh, initPullToRefresh } from "../pullToRefresh"
Cookies.set("browser.timezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
window.Cookies = Cookies

if (document.location.hostname.match(/spark.golf$/)) {
  window.CookieDomain = "spark.golf"
} else if (document.location.hostname.match(/sparkgolf.dev$/)) {
  window.CookieDomain = "sparkgolf.dev"
} else if (document.location.hostname === "localhost") {
  window.CookieDomain = null
} else {
  window.CookieDomain = document.location.hostname
}

window.setPermanentCookie = function (name, value) {
  let attributes = { expires: 365 * 20 }

  if (CookieDomain) {
    attributes.domain = CookieDomain
  }

  Cookies.set(name, value, attributes)
}

document.addEventListener("turbo:before-visit", destroyPullToRefresh)
document.addEventListener("turbo:before-render", destroyPullToRefresh)
document.addEventListener("turbo:before-cache", destroyPullToRefresh)

document.addEventListener("turbo:load", (event) => {
  // When a link with data-nav=push is clicked, we set sessionStorage.useHistoryBack to true.
  // Then when a page loads, turbo:load is triggered,
  // and if sessionStorage.useHistoryBack is true, we set window.useHistoryBack to true.
  // When the back arrow is clicked, we check if window.useHistoryBack is true,
  // and if it is, we call window.history.back()
  //
  // The purpose of this is to back is so we can have a link that can tell the next page
  // to use the browser's back button to go back to the previous page,
  // but if we navigate off that next page, we don't want to use the browser's back button anymore.
  if (typeof Storage !== "undefined") {
    window.useHistoryBack = sessionStorage.getItem("useHistoryBack") === "true"
    sessionStorage.removeItem("useHistoryBack")
  } else {
    window.useHistoryBack = false
  }

  const backArrow = document.getElementById("back-arrow")
  if (backArrow) {
    backArrow.addEventListener("click", (event) => {
      if (window.useHistoryBack) {
        event.preventDefault()
        event.stopPropagation()
        window.history.back()
      }
    })
  }

  if (typeof ga === "function") {
    ga("set", "location", event.detail.url)
    ga("send", "pageview")
  }

  document.querySelectorAll("[data-react-component]").forEach((element) => {
    const component = components[element.dataset.reactComponent]
    if (component) {
      const props = element.dataset.reactProps ? JSON.parse(element.dataset.reactProps) : {}
      const root = createRoot(element)
      root.render(React.createElement(component, props))
    } else {
      console.warn(`React Component ${element.dataset.reactComponent} is not registered`)
    }
  })

  initPullToRefresh()

  // Easily toggle things like modals
  // TODO consider moving this to a separate file if
  // we don't switch more things to react soon
  document.querySelectorAll("[data-toggle]").forEach((trigger) => {
    const { toggle } = trigger.dataset

    trigger.addEventListener("click", (event) => {
      event.preventDefault()

      // Find the target ID
      const target = document.querySelector(`#${toggle}`)

      // Toggle it
      if (target) {
        target.classList.toggle("show")
        document.body.classList.toggle(`${toggle}--open`)
      }
    })
  })

  // Simple JS accordion
  document.querySelectorAll(".accordion-title").forEach((item) => {
    item.addEventListener("click", (e) => {
      item.parentElement.classList.toggle("active")
    })
  })

  // App navigation scrolling behavior
  const nav = document.getElementById("app-nav")

  if (nav) {
    let didScroll = true
    window.onscroll = () => (didScroll = true)

    const scrollCheck = setInterval(() => {
      if (didScroll) {
        didScroll = false

        const top = window.pageYOffset || document.documentElement.scrollTop

        if (top > 72) {
          nav.classList.add("app-nav--is-scrolled")
        } else {
          nav.classList.remove("app-nav--is-scrolled")
        }
      }
    }, 100)

    document.addEventListener(
      "turbo:before-visit",
      () => {
        clearInterval(scrollCheck)
      },
      { once: true }
    )
  }

  // Initialize Swiper for cards on home screen
  new Swiper(".home-header.swiper", {
    grabCursor: true,
    spaceBetween: 16,
    slidesPerView: "auto"
  })

  // Initialize Swiper for feed posts
  new Swiper(".feed-post .swiper", {
    grabCursor: true,
    pagination: {
      el: ".swiper-pagination",
      clickable: true
    }
  })

  // Initialize Swiper for upcoming rounds cards on league overview
  new Swiper(".upcoming-rounds.swiper", {
    grabCursor: true,
    spaceBetween: 16,
    slidesPerView: "auto"
  })

  // Initialize Swiper for hole sponsor cards on league overview
  new Swiper(".hole-sponsors.swiper", {
    grabCursor: true,
    spaceBetween: 16,
    slidesPerView: "auto"
  })
})

window.toggleDisplay = function (selector) {
  let el = document.querySelector(selector)
  if (el) {
    if (el.style.display === "none") {
      el.style.display = ""
    } else {
      el.style.display = "none"
    }
  }
}

document.addEventListener("click", async (event) => {
  const feedPostLike = event.target.closest("[data-like-for-feed-post-sqid]")

  if (feedPostLike) {
    event.preventDefault()
    event.stopPropagation()

    console.log("feedPostLike", feedPostLike)
    console.log("feedPostLike.parentNode", feedPostLike.parentNode)

    const sqid = feedPostLike.dataset.likeForFeedPostSqid
    const liked = feedPostLike.parentNode.dataset.liked === "true"

    if (feedPostLike.parentNode.classList.contains("loading")) return

    feedPostLike.parentNode.classList.add("loading")

    try {
      const response = await fetch(`/app/p/${sqid}/like`, {
        method: liked ? "DELETE" : "POST"
      })

      if (response.ok) {
        feedPostLike.parentNode.outerHTML = await response.text()
      }
    } catch (error) {
      console.error(error)
    } finally {
      feedPostLike.parentNode.classList.remove("loading")
    }
    return
  }

  const navLink = event.target.closest("a[data-nav]")

  if (typeof Storage !== "undefined" && navLink) {
    const nav = navLink.dataset.nav

    if (nav === "push") {
      sessionStorage.setItem("useHistoryBack", "true")
    }
    return
  }

  const shareLink = event.target.closest("[data-share-title]")

  if (shareLink && navigator.share) {
    event.preventDefault()
    event.stopPropagation()
    await navigator.share({
      title: shareLink.dataset.shareTitle,
      text: shareLink.dataset.shareText,
      url: shareLink.href
    })
  }
})

window.hideToast = (event) => {
  event.preventDefault()
  const toast = document.getElementById("toast")
  if (toast) {
    toast.classList.add("fade-out")
    setTimeout(() => {
      toast.remove()
    }, 1000)
  }
}

window.openInExternalWindow = (el, event) => {
  if (Median && Median.isNativeApp()) {
    event.preventDefault()
    event.stopPropagation()
    Median.window.open(el.href, "external")
  }
}
